import 'react-native-get-random-values';
import RNSplashScreen from 'react-native-splash-screen';
import { v4 as uuid4 } from 'uuid';
import { Platform, SafeAreaView, StyleSheet, Text } from 'react-native';
import React, { Component } from 'react';

import { AppRouter } from '@providers/Router';
import { DirectionProvider } from '@providers/Direction/DirectionProvider';
import { I18nProvider } from '@providers/I18n';
import { SplashScreen } from '@components/SplashScreen';
import { ThemeProvider } from '@providers/Theme';
import { ToastProvider } from '@providers/Toast';
import { crashlyticsUtil } from '@lib/crashlytics';
import { logger } from '@lib/logger';
import { trackEvent } from '@utils/analytics';
import { getData, setData } from '@lib/storage';

import '@lib/firebase';

const appStyles = StyleSheet.create({
  root: {
    flex: 1,
    height: '100%',
  },
});

(async function setupDeviceId() {
  const currentId = await getData('deviceId');
  if (!currentId) {
    setData('deviceId', uuid4());
  }
})();

type AppProps = {};
export class App extends Component<AppProps, { error: Error | null }> {
  constructor(props: AppProps) {
    super(props);
    this.state = { error: null };
  }

  /* ------------------ Web-Specific Handlers ------------------ */

  private handleWindowError = (event: ErrorEvent) => {
    const { message, filename: source, lineno, colno, error } = event;

    trackEvent('web_js_error', {
      message,
      source,
      lineno,
      colno,
      stack: error?.stack?.substring(0, 1000),
      lastAction: window.__LAST_ACTION__,
      lastOrder: window.__LAST_ORDER__,
      url: window.location.href,
    });
  };

  private handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    trackEvent('web_promise_error', {
      message: (event.reason as any)?.message || 'Unhandled promise rejection',
      stack: (event.reason as any)?.stack?.substring(0, 1000),
      lastAction: window.__LAST_ACTION__,
      lastOrder: window.__LAST_ORDER__,
    });
  };

  /* ------------------ Lifecycle Hooks ------------------ */

  componentDidMount() {
    if (Platform.OS === 'web') {
      window.addEventListener('error', this.handleWindowError);
      window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
    } else {
      // NATIVE MOBILE: Catch unhandled async/await promise rejections safely
      const promise = require('promise/setimmediate/rejection-tracking');
      promise.enable({
        allRejections: true,
        onUnhandled: (id: any, error: any) => {
          logger.error('Unhandled Native Promise Rejection:', error);

          crashlyticsUtil.setAttribute('error_source', 'Unhandled_Promise_Rejection');
          crashlyticsUtil.recordError(
            error instanceof Error ? error : new Error(String(error || 'Unknown Async Error'))
          );
        },
      });
    }
  }

  componentWillUnmount() {
    if (Platform.OS === 'web') {
      window.removeEventListener('error', this.handleWindowError);
      window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
    }
  }

  /* ------------------ Error Boundary Handling ------------------ */

  componentDidCatch(err: Error, info: React.ErrorInfo) {
    const errorMessage = String(err?.message || 'Unknown Error');
    const errorStack = err?.stack ? String(err.stack).replace(/\n/g, ' | ') : '';
    const componentStack = info?.componentStack ? String(info.componentStack).replace(/\n/g, ' | ') : '';

    logger.error('Global error caught:', {
      message: errorMessage,
      stack: errorStack,
      componentStack,
    });

    trackEvent('app_crash', {
      error_message: errorMessage,
      name: err.name,
      stack: errorStack.substring(0, 1000),
      component_stack: componentStack.substring(0, 1000),
      is_fatal: 'true',
      last_action: Platform.OS === 'web' ? window.__LAST_ACTION__ : undefined,
    });

    if (Platform.OS !== 'web') {
      crashlyticsUtil.log(`React Error: ${err.name}`);
      crashlyticsUtil.log(`Message: ${err.message}`);

      if (err.stack) {
        crashlyticsUtil.log(err.stack.substring(0, 1000));
      }

      if (info.componentStack) {
        crashlyticsUtil.log(info.componentStack.substring(0, 1000));
      }

      crashlyticsUtil.recordError(err);
      RNSplashScreen.hide();
    }
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      return (
        <Text>
          Something went wrong! Try refreshing the application.
        </Text>
      );
    }

    return (
      <ThemeProvider>
        <I18nProvider>
          <DirectionProvider>
            <ToastProvider>
              <SplashScreen>
                <SafeAreaView style={appStyles.root}>
                  <AppRouter />
                </SafeAreaView>
              </SplashScreen>
            </ToastProvider>
          </DirectionProvider>
        </I18nProvider>
      </ThemeProvider>
    );
  }
}
